'use client';

import React, { useEffect, useState, useContext, useRef } from 'react';

import { SCROLL_DIRECTION } from '../../constants/constants';
import { OptionsContext } from '@/providers/options';
import { useUser } from '@/utils/supabase/useUser';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';

import Banners from '../Ads/Banners';
import NavBar from '../NavBar';
import { differenceInDays } from 'date-fns';

// const checkAppBodyPosition = () => {
//   if (typeof document === 'undefined') return 0;
//   const appBody = document?.getElementById('app-body') ?? null;
//   if (!appBody) return 0;
//   return appBody.offsetTop;// - 200;
// };

const Header = ({
  menu,
  banner
}: {
  menu: Menu | null;
  banner?: Banner | null;
}) => {
  const { user } = useUser();
  const { alwaysFixed, articleName, slug, contentType } =
    useContext(OptionsContext);

  const [sectionBoxOpen, setSectionBoxOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const [fixedNav, setFixedNav] = useState<boolean>(alwaysFixed);
  const [menuHeight, setMenuHeight] = useState(280);

  const [scrollDirection, setScrollDirection] = useState(SCROLL_DIRECTION.UP);

  const isAuthenticatedState = user !== null;

  const daysSinceConfirm = differenceInDays(
    new Date(),
    new Date(user?.email_confirmed_at ?? '')
  );

  const userRoles = user?.app_metadata.roles;
  const showWelcomeHighlight =
    !!isAuthenticatedState &&
    daysSinceConfirm < 7 &&
    (userRoles?.includes('professional') || userRoles?.includes('executive'));

  const roles = (user?.app_metadata.roles as string[]) ?? [];

  const isProfessionalMember = roles.includes('professional');

  // useEffect(() => {
  //   setBodyOffset(checkAppBodyPosition());
  // }, []);

  useScrollPosition(
    ({ prevPos, currPos }) => {
      const diff = currPos.y - prevPos.y;
      if (diff < -3 && scrollDirection !== SCROLL_DIRECTION.DOWN) {
        setScrollDirection(SCROLL_DIRECTION.DOWN);
      }
      if (diff > 3 && scrollDirection !== SCROLL_DIRECTION.UP) {
        setScrollDirection(SCROLL_DIRECTION.UP);
      }

      if (currPos.y < -1 * menuHeight && !fixedNav) {
        setFixedNav(true);
      } else if (currPos.y > -1 * menuHeight && fixedNav) {
        setFixedNav(false);
      }
    },
    [fixedNav, scrollDirection]
  );

  //#region Menu resize handling

  const menuContainerRef = useRef<HTMLDivElement>(null);
  const updateBodyTop = () => {
    // Only update the menu container placeholder's height when the nav is expanded to its full height, to avoid unnecessary dynamic resizing
    // If the nav is currently fixed (and not set to always-fixed mode), or if the container ref is not currently set, skip this update
    if ((fixedNav && !alwaysFixed) || !menuContainerRef.current) {
      return;
    }
    const height = menuContainerRef.current.clientHeight;
    setMenuHeight(height);
  };
  useEffect(() => {
    updateBodyTop();
  }, []);

  // Handle menu container element resizing on a delay, to prevent excessive dynamic updates during an active resize
  // Once the resize timeout expires, trigger updateBodyTop()
  const menuResizeTimeout = useRef<number | null>(null);
  const handleMenuResize = () => {
    if (menuResizeTimeout.current) {
      clearTimeout(menuResizeTimeout.current);
      menuResizeTimeout.current = null;
    }

    menuResizeTimeout.current = window.setTimeout(() => {
      updateBodyTop();

      if (menuResizeTimeout.current) {
        clearTimeout(menuResizeTimeout.current);
        menuResizeTimeout.current = null;
      }
    }, 250);

    return () => {
      menuResizeTimeout.current && clearTimeout(menuResizeTimeout.current);
    };
  };

  // Attach a resize observer to the menu container element, triggering the menu resize handler any time the container's size updates
  useEffect(() => {
    if (!menuContainerRef.current) return undefined;

    handleMenuResize();

    const resizeObserver = new ResizeObserver((el) => handleMenuResize);
    resizeObserver.observe(menuContainerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [menuContainerRef.current]);

  const [menuState, setMenuState] = useState<'up' | 'down'>('up');
  useEffect(() => {
    setMenuState(
      scrollDirection === SCROLL_DIRECTION.UP || sectionBoxOpen || searchOpen
        ? 'up'
        : 'down'
    );
  }, [scrollDirection, sectionBoxOpen, searchOpen]);

  //#endregion Menu resize handling

  return (
    <>
      <div
        className={`app-header${fixedNav ? ' app-header__fixed' : ''} ${menuState}`}
      >
        <div className='menu-container' ref={menuContainerRef}>
          <NavBar
            position='top'
            fixed={fixedNav}
            articleName={articleName}
            slug={slug}
            contentType={contentType}
            isAuthenticated={isAuthenticatedState}
            isProfessionalMember={isProfessionalMember}
            sectionBoxOpen={sectionBoxOpen}
            setSectionBoxOpen={setSectionBoxOpen}
            searchOpen={searchOpen}
            setSearchOpen={setSearchOpen}
            menu={menu}
            showWelcomeHighlight={showWelcomeHighlight}
          />
          {banner && <Banners banner={banner} />}
        </div>
      </div>
      <div className='app-header-placeholder' style={{ height: menuHeight }} />
      {/* if need, import UrbanityBanner and uncoment line below to show it */}
    </>
  );
};

export default Header;
